import React, { useState, useEffect } from 'react';
import {
  Steps,
  Card,
  Button,
  List,
  Result,
  Spin,
  Alert,
  Typography,
  Input,
  Select,
} from 'antd';
import {
  SafetyOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { generateCodeVerifier, generateCodeChallenge } from '../../utils/pkce';
import { history, navigateTo } from '../../utils';
import {
  selectSharePointSites,
  selectSharePointDrives,
  selectSelectedSharePointSite,
  selectSelectedSharePointDrive,
  selectSharePointLoading,
  selectSharePointSaving,
  selectSharePointError,
  selectSharePointFolderPath,
  selectSharePointGrantResult,
} from '../../redux/store/userData/sharepoint/selectors';
import {
  setSelectedSite,
  setSelectedDrive,
  setFolderPath,
  sharepointReset,
} from '../../redux/store/userData/sharepoint/actions';
import {
  fetchSharePointSites,
  lookupSharePointSiteByUrl,
  fetchSharePointDrives,
  grantPermissionAndSave,
} from '../../redux/store/userData/sharepoint/thunk';
import {
  Site,
  Drive,
} from '../../redux/store/userData/sharepoint/initialState';
import ListItem from '../shared/ListItem';
import styles from './SharePointSetupWizard.styles';

const { Text, Paragraph } = Typography;

const CLIENT_ID = process.env.VITE_SHAREPOINT_CLIENT_ID || '';

const SharePointSetupWizard = () => {
  const params = new URLSearchParams(window.location.search);
  const initialStep = Number(params.get('step')) || 0;

  const dispatch = useDispatch();
  const intl = useIntl();

  // Local UI state
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadType, setUploadType] = useState<
    'signupCertificate' | 'accounting'
  >(
    (sessionStorage.getItem('sp_upload_type') as
      | 'signupCertificate'
      | 'accounting') || 'accounting'
  );

  const handleUploadTypeChange = (
    value: 'signupCertificate' | 'accounting'
  ) => {
    setUploadType(value);
    sessionStorage.setItem('sp_upload_type', value);
  };

  // Redux state
  const sites = useSelector(selectSharePointSites);
  const selectedSite = useSelector(selectSelectedSharePointSite);
  const drives = useSelector(selectSharePointDrives);
  const selectedDrive = useSelector(selectSelectedSharePointDrive);
  const folderPath = useSelector(selectSharePointFolderPath);
  const loading = useSelector(selectSharePointLoading);
  const saving = useSelector(selectSharePointSaving);
  const error = useSelector(selectSharePointError);
  const grantResult = useSelector(selectSharePointGrantResult);

  // Session storage (transient OAuth state)
  const hasToken = !!sessionStorage.getItem('sp_access_token');
  const hasAdminConsent = sessionStorage.getItem('sp_admin_consent') === 'true';
  const tenantId = sessionStorage.getItem('sp_tenant_id');

  // Auto-fetch all sites when entering step 1
  useEffect(() => {
    if (currentStep === 1 && hasToken && sites.length === 0) {
      dispatch(fetchSharePointSites('*') as any);
    }
  }, [currentStep]);

  // Fetch document libraries when entering step 2
  useEffect(() => {
    if (currentStep === 2 && selectedSite && drives.length === 0) {
      dispatch(fetchSharePointDrives(selectedSite.id) as any);
    }
  }, [currentStep]);

  const handleAdminConsent = () => {
    if (!CLIENT_ID) return;

    const state = crypto.randomUUID();
    sessionStorage.setItem('sp_oauth_state', state);
    sessionStorage.setItem('sp_client_id', CLIENT_ID);

    const redirectUri = `${window.location.origin}/sharepoint-callback`;
    const consentUrl =
      `https://login.microsoftonline.com/organizations/v2.0/adminconsent?` +
      new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri,
        state,
        scope: 'https://graph.microsoft.com/.default',
      }).toString();

    window.location.href = consentUrl;
  };

  const handleAuthorize = async () => {
    if (!CLIENT_ID) return;

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = crypto.randomUUID();

    sessionStorage.setItem('sp_code_verifier', codeVerifier);
    sessionStorage.setItem('sp_oauth_state', state);
    sessionStorage.setItem('sp_client_id', CLIENT_ID);
    sessionStorage.setItem('sp_flow', 'pkce');

    const redirectUri = `${window.location.origin}/sharepoint-callback`;
    const authUrl =
      `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?` +
      new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: redirectUri,
        scope:
          'https://graph.microsoft.com/Sites.FullControl.All https://graph.microsoft.com/Sites.Read.All offline_access',
        response_mode: 'query',
        prompt: 'consent',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      }).toString();

    window.location.href = authUrl;
  };

  const handleConfirmSite = () => {
    if (!selectedSite || !selectedDrive) return;
    dispatch(
      grantPermissionAndSave({
        selectedSite,
        selectedDrive,
        folderPath,
        tenantId,
        uploadType,
      }) as any
    );
  };

  const handleReset = () => {
    sessionStorage.removeItem('sp_access_token');
    sessionStorage.removeItem('sp_refresh_token');
    sessionStorage.removeItem('sp_tenant_id');
    sessionStorage.removeItem('sp_client_id');
    sessionStorage.removeItem('sp_code_verifier');
    sessionStorage.removeItem('sp_oauth_state');
    sessionStorage.removeItem('sp_admin_consent');
    sessionStorage.removeItem('sp_flow');
    sessionStorage.removeItem('sp_upload_type');
    dispatch(sharepointReset());
    setCurrentStep(0);
    setSearchQuery('');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ textAlign: 'center' }}>
            {hasToken ? (
              <Result
                status="success"
                title={intl.formatMessage({
                  id: 'fleetadmin.sharepoint.auth.success.title',
                })}
                subTitle={intl.formatMessage({
                  id: 'fleetadmin.sharepoint.auth.success.subtitle',
                })}
                extra={
                  <>
                    {tenantId && (
                      <ListItem
                        label={intl.formatMessage({
                          id: 'fleetadmin.sharepoint.auth.tenantId',
                        })}
                        value={tenantId}
                      />
                    )}
                    <Button
                      className="submit-button"
                      onClick={() => setCurrentStep(1)}
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.continue',
                      })}
                    </Button>
                  </>
                }
              />
            ) : (
              <>
                {!CLIENT_ID && (
                  <Alert
                    message={intl.formatMessage({
                      id: 'fleetadmin.sharepoint.auth.envWarning',
                    })}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {error && (
                  <Alert
                    message={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {!hasAdminConsent ? (
                  <>
                    <Paragraph>
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.consent.description',
                      })}
                    </Paragraph>
                    <div className="upload-type-row">
                      <Text strong className="upload-type-label">
                        Upload Type
                      </Text>
                      <Select
                        value={uploadType}
                        onChange={handleUploadTypeChange}
                        style={{ width: 240 }}
                        options={[
                          { value: 'accounting', label: 'Accounting' },
                          {
                            value: 'signupCertificate',
                            label: 'Signup Certificate',
                          },
                        ]}
                      />
                    </div>
                    <Button
                      className="submit-button"
                      onClick={handleAdminConsent}
                      disabled={!CLIENT_ID}
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.consent.button',
                      })}
                    </Button>
                  </>
                ) : (
                  <>
                    <Alert
                      message={intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.consent.granted',
                      })}
                      type="success"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Paragraph>
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.authorize.description',
                      })}
                    </Paragraph>
                    <Button
                      className="submit-button"
                      onClick={handleAuthorize}
                      disabled={!CLIENT_ID}
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.auth.authorize.button',
                      })}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        );

      case 1:
        return (
          <div>
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>
                  {intl.formatMessage({
                    id: 'fleetadmin.sharepoint.sites.loading',
                  })}
                </p>
              </div>
            ) : (
              <>
                <Paragraph>
                  {intl.formatMessage({
                    id: 'fleetadmin.sharepoint.sites.description',
                  })}
                </Paragraph>
                <Input.Search
                  placeholder={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.sites.placeholder',
                  })}
                  enterButton={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.sites.lookup',
                  })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={(value) => {
                    if (value.startsWith('http')) {
                      dispatch(lookupSharePointSiteByUrl(value) as any);
                    } else {
                      dispatch(fetchSharePointSites(value) as any);
                    }
                  }}
                  style={{ marginBottom: 16 }}
                />
                {sites.length > 0 && (
                  <List
                    bordered
                    dataSource={sites}
                    renderItem={(site: Site) => (
                      <List.Item
                        onClick={() => dispatch(setSelectedSite(site))}
                        style={{
                          cursor: 'pointer',
                          background:
                            selectedSite?.id === site.id
                              ? '#e6f4ff'
                              : undefined,
                        }}
                      >
                        <List.Item.Meta
                          title={site.displayName || site.name}
                          description={site.webUrl}
                        />
                        {selectedSite?.id === site.id && (
                          <CheckCircleOutlined
                            style={{ color: '#1677ff', fontSize: 20 }}
                          />
                        )}
                      </List.Item>
                    )}
                  />
                )}
                {selectedSite && (
                  <div style={{ marginTop: 16 }}>
                    <Button
                      className="submit-button"
                      onClick={() => {
                        dispatch(setSelectedDrive(null));
                        setCurrentStep(2);
                      }}
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.sites.continue',
                      })}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div>
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>
                  {intl.formatMessage({
                    id: 'fleetadmin.sharepoint.drives.loading',
                  })}
                </p>
              </div>
            ) : (
              <>
                <Paragraph>
                  {intl.formatMessage(
                    { id: 'fleetadmin.sharepoint.drives.description' },
                    { siteName: selectedSite?.displayName }
                  )}
                </Paragraph>
                {drives.length > 0 && (
                  <List
                    bordered
                    dataSource={drives}
                    renderItem={(drive: Drive) => (
                      <List.Item
                        onClick={() => dispatch(setSelectedDrive(drive))}
                        style={{
                          cursor: 'pointer',
                          background:
                            selectedDrive?.id === drive.id
                              ? '#e6f4ff'
                              : undefined,
                        }}
                      >
                        <List.Item.Meta
                          title={drive.name}
                          description={
                            <>
                              {drive.description && (
                                <span>{drive.description} — </span>
                              )}
                              <Text type="secondary">{drive.driveType}</Text>
                            </>
                          }
                        />
                        {selectedDrive?.id === drive.id && (
                          <CheckCircleOutlined
                            style={{ color: '#1677ff', fontSize: 20 }}
                          />
                        )}
                      </List.Item>
                    )}
                  />
                )}
                {drives.length === 0 && !loading && (
                  <Alert
                    message={intl.formatMessage({
                      id: 'fleetadmin.sharepoint.drives.empty',
                    })}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {selectedDrive && (
                  <div style={{ marginTop: 16 }}>
                    <Paragraph>
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.drives.folderPath',
                      })}
                    </Paragraph>
                    <Input
                      placeholder={intl.formatMessage({
                        id: 'fleetadmin.sharepoint.drives.folderPlaceholder',
                      })}
                      value={folderPath}
                      onChange={(e) => dispatch(setFolderPath(e.target.value))}
                      style={{ marginBottom: 16 }}
                    />
                    <Button
                      className="submit-button"
                      onClick={() => setCurrentStep(3)}
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.sharepoint.drives.continue',
                      })}
                    </Button>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <Button
                    className="cancel-button"
                    onClick={() => setCurrentStep(1)}
                  >
                    {intl.formatMessage({
                      id: 'fleetadmin.sharepoint.drives.back',
                    })}
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div>
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {grantResult ? (
              <>
                <Result
                  status="success"
                  title={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.title',
                  })}
                  subTitle={
                    grantResult.permissionGranted
                      ? intl.formatMessage({
                          id: 'fleetadmin.sharepoint.confirm.success',
                        })
                      : intl.formatMessage({
                          id: 'fleetadmin.sharepoint.confirm.warning',
                        })
                  }
                  style={{ paddingBottom: 8 }}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.site',
                  })}
                  value={selectedSite?.displayName}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.library',
                  })}
                  value={selectedDrive?.name}
                />
                {folderPath && (
                  <ListItem
                    label={intl.formatMessage({
                      id: 'fleetadmin.sharepoint.confirm.path',
                    })}
                    value={folderPath}
                  />
                )}
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.siteId',
                  })}
                  value={selectedSite?.id.split(',')[2] || selectedSite?.id}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.siteHost',
                  })}
                  value={selectedSite?.id.split(',')[0]}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.driveId',
                  })}
                  value={selectedDrive?.id}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.tenantId',
                  })}
                  value={tenantId}
                />
                <ListItem
                  label="Upload Type"
                  value={
                    uploadType === 'signupCertificate'
                      ? 'Signup Certificate'
                      : 'Accounting'
                  }
                />
                <Button
                  className="submit-button"
                  onClick={() => {
                    navigateTo(history, '/dashboard?vm=VEHICLE&type=TABLE');
                    window.location.reload();
                  }}
                >
                  Go to Dashboard
                </Button>
              </>
            ) : (
              <div>
                <Paragraph>
                  {intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.description',
                  })}
                </Paragraph>
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.site',
                  })}
                  value={selectedSite?.displayName}
                />
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.library',
                  })}
                  value={selectedDrive?.name}
                />
                {folderPath && (
                  <ListItem
                    label={intl.formatMessage({
                      id: 'fleetadmin.sharepoint.confirm.path',
                    })}
                    value={folderPath}
                  />
                )}
                <ListItem
                  label={intl.formatMessage({
                    id: 'fleetadmin.sharepoint.confirm.siteUrl',
                  })}
                  value={selectedSite?.webUrl}
                />
                <ListItem
                  label="Upload Type"
                  value={
                    uploadType === 'signupCertificate'
                      ? 'Signup Certificate'
                      : 'Accounting'
                  }
                />
                <div style={{ marginTop: 16 }}>
                  <Button
                    className="submit-button"
                    loading={saving}
                    onClick={handleConfirmSite}
                  >
                    {intl.formatMessage({
                      id: 'fleetadmin.sharepoint.confirm.button',
                    })}
                  </Button>
                  <Button
                    className="cancel-button"
                    onClick={() => setCurrentStep(2)}
                  >
                    {intl.formatMessage({
                      id: 'fleetadmin.sharepoint.confirm.back',
                    })}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="sharepoint-wizard">
        <Card
          title={intl.formatMessage({ id: 'fleetadmin.sharepoint.title' })}
          extra={
            hasToken && (
              <Button size="small" danger onClick={handleReset}>
                {intl.formatMessage({
                  id: 'fleetadmin.sharepoint.button.reset',
                })}
              </Button>
            )
          }
          style={{ maxWidth: 720, width: '100%' }}
        >
          <Steps
            current={currentStep}
            style={{ marginBottom: 32 }}
            items={[
              {
                title: intl.formatMessage({
                  id: 'fleetadmin.sharepoint.step.authorize',
                }),
                icon: <SafetyOutlined />,
              },
              {
                title: intl.formatMessage({
                  id: 'fleetadmin.sharepoint.step.selectSite',
                }),
                icon: <GlobalOutlined />,
              },
              {
                title: intl.formatMessage({
                  id: 'fleetadmin.sharepoint.step.selectLibrary',
                }),
                icon: <FolderOutlined />,
              },
              {
                title: intl.formatMessage({
                  id: 'fleetadmin.sharepoint.step.confirm',
                }),
                icon: <CheckCircleOutlined />,
              },
            ]}
          />
          {renderStepContent()}
        </Card>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default SharePointSetupWizard;
